import { useEffect, useRef } from 'react';
import {
  defaultKeybindingsHandlerIgnore,
  tinykeys,
  type KeybindingsMap,
} from 'tinykeys';

interface ShortcutOptions {
  enabled?: boolean;
  allowInDialogs?: boolean;
}

/**
 * Keeps one tinykeys subscription stable while dispatching to the latest
 * React closures. Form controls are ignored by tinykeys; feature scopes also
 * pause while a dialog or menu owns the keyboard.
 */
export function useKeyboardShortcuts(
  bindings: KeybindingsMap,
  { enabled = true, allowInDialogs = false }: ShortcutOptions = {},
): void {
  const bindingsRef = useRef(bindings);
  bindingsRef.current = bindings;
  const signature = Object.keys(bindings).join('\n');

  useEffect(() => {
    if (!enabled) return;
    const currentBindings = bindingsRef.current;
    const proxies = Object.fromEntries(
      Object.keys(currentBindings).map((key) => [
        key,
        (event: KeyboardEvent) => bindingsRef.current[key]?.(event),
      ]),
    );

    return tinykeys(window, proxies, {
      ignore: (event) =>
        defaultKeybindingsHandlerIgnore(event) ||
        (!allowInDialogs &&
          event.target instanceof Element &&
          event.target.closest('[role="dialog"], [role="menu"]') !== null),
    });
  }, [allowInDialogs, enabled, signature]);
}
